import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useSearchParams } from "react-router-dom";
import { toast } from '../../../components/ui/notify';
import AboutOrganization from "./AboutOrganization";
import FAQ from "./FAQ";
import Notifications from "./Notifications";
import Query from "./Query";
import PaymentHistory from "./PaymentHistory";
import Announcement from "../announcement/announcement";
import DeletedAccounts from "./DeletedAccounts";
import { usePermissions } from "../../../context/PermissionsContext";
import { isFeedFeatureEnabled } from "../../../utils/featureFlags";

function Settings() {
  const { permissions } = usePermissions();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("about-organization");
  const [, setAccountSettingsClickCount] = useState(0);
  const [showMasterNotificationControl, setShowMasterNotificationControl] = useState(false);
  const isPaymentEnabled = isFeedFeatureEnabled();

  useEffect(() => {
    // Get activeTab from URL query parameter
    const tabFromUrl = searchParams.get("activeTab");
    if (tabFromUrl === "video-playback" || (tabFromUrl === "payment-history" && !isPaymentEnabled)) {
      setActiveTab("about-organization");
      return;
    }
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, isPaymentEnabled]);

  useEffect(() => {
    if (activeTab !== "notifications") {
      setAccountSettingsClickCount(0);
      setShowMasterNotificationControl(false);
    }
  }, [activeTab]);

  const handleTabChange = (tab) => {
    // Check permissions before changing tab
    switch (tab) {
      case 'about-organization':
        if (!permissions.settings_about_organization_view) {
          toast.warning("You don't have permission to view organization information");
          return;
        }
        break;
      case 'faq':
        if (!permissions.settings_faq_view) {
          toast.warning("You don't have permission to view FAQs");
          return;
        }
        break;
      case 'notifications':
        if (!permissions.notification_management_view) {
          toast.warning("You don't have permission to manage notifications");
          return;
        }
        break;
      case 'query':
        if (!permissions.settings_query_view) {
          toast.warning("You don't have permission to view queries");
          return;
        }
        break;
      case 'payment-history':
        if (!isPaymentEnabled) {
          return;
        }
        if (!permissions.settings_payments_view) {
          toast.warning("You don't have permission to view payment history");
          return;
        }
        break;
      case 'announcement':
        if (!permissions.announcement_management_view) {
          toast.warning("You don't have permission to manage announcements");
          return;
        }
        break;
      case 'deleted-accounts':
        if (!permissions['settings_account_deletion-view']) {
          toast.warning("You don't have permission to view deleted accounts");
          return;
        }
        break;
      default:
        break;
    }

    setActiveTab(tab);
  };

  const handleAccountSettingsClick = () => {
    handleTabChange("notifications");

    if (!permissions.notification_management_view || showMasterNotificationControl) {
      return;
    }

    setAccountSettingsClickCount((currentCount) => {
      const nextCount = Math.min(currentCount + 1, 4);
      if (nextCount === 4) {
        setShowMasterNotificationControl(true);
      }
      return nextCount;
    });
  };

  return (
    <div className="contianer">
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
        <div className="tw-min-w-[0px] ui-column tw-w-full">
          <div className="tw-min-h-full max-[768px]:tw-p-[16px] settings-container">
            {/* Settings Tabs */}
            <div className="tw-mb-0 settings-tabs-container">
              <div className={"tw-flex tw-border-b-[1px] tw-border-solid tw-border-b-[#e0e0e0] tw-overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden settings-tabs"}>
                <button
                  className={`tw-p-[12px_20px] tw-text-[15px] tw-font-medium tw-text-[#666] tw-bg-[none] tw-border-0 tw-border-b-[3px] tw-border-solid tw-border-b-[transparent] tw-cursor-pointer tw-whitespace-nowrap [transition:all_0.2s_ease] [&:hover]:tw-text-[#3152e8] [&.active]:tw-text-[#3152e8] [&.active]:tw-border-b-[#3152e8] max-[768px]:tw-p-[10px_16px] max-[768px]:tw-text-[14px] settings-tab-item   ${activeTab === "about-organization" ? "active" : ""}`}
                  onClick={() => handleTabChange('about-organization')}
                  disabled={!permissions.settings_about_organization_view}
                  title={!permissions.settings_about_organization_view ? "You don't have permission to view organization information" : ""}
                >
                  <Icon
                    icon="fluent:building-24-regular"
                    width="22"
                    height="22"
                    className="tw-me-2"
                  />
                  About Organization
                </button>

                <button
                  className={`tw-p-[12px_20px] tw-text-[15px] tw-font-medium tw-text-[#666] tw-bg-[none] tw-border-0 tw-border-b-[3px] tw-border-solid tw-border-b-[transparent] tw-cursor-pointer tw-whitespace-nowrap [transition:all_0.2s_ease] [&:hover]:tw-text-[#3152e8] [&.active]:tw-text-[#3152e8] [&.active]:tw-border-b-[#3152e8] max-[768px]:tw-p-[10px_16px] max-[768px]:tw-text-[14px] settings-tab-item   ${activeTab === "faq" ? "active" : ""}`}
                  onClick={() => handleTabChange("faq")}
                  disabled={!permissions.settings_faq_view}
                  title={!permissions.settings_faq_view ? "You don't have permission to view FAQs" : ""}
                >
                  <Icon
                    icon="fluent:question-circle-24-regular"
                    width="22"
                    height="22"
                    className="tw-me-2"
                  />
                  FAQ
                </button>

                <button
                  className={`tw-p-[12px_20px] tw-text-[15px] tw-font-medium tw-text-[#666] tw-bg-[none] tw-border-0 tw-border-b-[3px] tw-border-solid tw-border-b-[transparent] tw-cursor-pointer tw-whitespace-nowrap [transition:all_0.2s_ease] [&:hover]:tw-text-[#3152e8] [&.active]:tw-text-[#3152e8] [&.active]:tw-border-b-[#3152e8] max-[768px]:tw-p-[10px_16px] max-[768px]:tw-text-[14px] settings-tab-item   ${activeTab === "notifications" ? "active" : ""}`}
                  onClick={handleAccountSettingsClick}
                  disabled={!permissions.notification_management_view}
                  title={!permissions.notification_management_view ? "You don't have permission to manage notifications" : ""}
                >
                  <Icon
                    icon="mdi:bell-cog-outline"
                    width="22"
                    height="22"
                    className="tw-me-2"
                  />
                  Account Settings
                </button>

                <button
                  className={`tw-p-[12px_20px] tw-text-[15px] tw-font-medium tw-text-[#666] tw-bg-[none] tw-border-0 tw-border-b-[3px] tw-border-solid tw-border-b-[transparent] tw-cursor-pointer tw-whitespace-nowrap [transition:all_0.2s_ease] [&:hover]:tw-text-[#3152e8] [&.active]:tw-text-[#3152e8] [&.active]:tw-border-b-[#3152e8] max-[768px]:tw-p-[10px_16px] max-[768px]:tw-text-[14px] settings-tab-item   ${activeTab === "announcement" ? "active" : ""}`}
                  onClick={() => handleTabChange("announcement")}
                  disabled={!permissions.announcement_management_view}
                  title={!permissions.announcement_management_view ? "You don't have permission to manage announcements" : ""}
                >
                  <Icon
                    icon="fluent:megaphone-loud-24-regular"
                    width="22"
                    height="22"
                    className="tw-me-2"
                  />
                  Announcements
                </button>

                <button
                  className={`tw-p-[12px_20px] tw-text-[15px] tw-font-medium tw-text-[#666] tw-bg-[none] tw-border-0 tw-border-b-[3px] tw-border-solid tw-border-b-[transparent] tw-cursor-pointer tw-whitespace-nowrap [transition:all_0.2s_ease] [&:hover]:tw-text-[#3152e8] [&.active]:tw-text-[#3152e8] [&.active]:tw-border-b-[#3152e8] max-[768px]:tw-p-[10px_16px] max-[768px]:tw-text-[14px] settings-tab-item   ${activeTab === "query" ? "active" : ""}`}
                  onClick={() => handleTabChange("query")}
                  disabled={!permissions.settings_query_view}
                  title={!permissions.settings_query_view ? "You don't have permission to view queries" : ""}
                >
                  <Icon
                    icon="fluent:chat-help-24-regular"
                    width="22"
                    height="22"
                    className="tw-me-2"
                  />
                  Support Query
                </button>

                {isPaymentEnabled && (
                  <button
                    className={`tw-p-[12px_20px] tw-text-[15px] tw-font-medium tw-text-[#666] tw-bg-[none] tw-border-0 tw-border-b-[3px] tw-border-solid tw-border-b-[transparent] tw-cursor-pointer tw-whitespace-nowrap [transition:all_0.2s_ease] [&:hover]:tw-text-[#3152e8] [&.active]:tw-text-[#3152e8] [&.active]:tw-border-b-[#3152e8] max-[768px]:tw-p-[10px_16px] max-[768px]:tw-text-[14px] settings-tab-item   ${activeTab === "payment-history" ? "active" : ""}`}
                    onClick={() => handleTabChange("payment-history")}
                    disabled={!permissions.settings_payments_view}
                    title={!permissions.settings_payments_view ? "You don't have permission to view payment history" : ""}
                  >
                    <Icon
                      icon="fluent:payment-24-regular"
                      width="22"
                      height="22"
                      className="tw-me-2"
                    />
                    Payment History
                  </button>
                )}

                <button
                  className={`tw-p-[12px_20px] tw-text-[15px] tw-font-medium tw-text-[#666] tw-bg-[none] tw-border-0 tw-border-b-[3px] tw-border-solid tw-border-b-[transparent] tw-cursor-pointer tw-whitespace-nowrap [transition:all_0.2s_ease] [&:hover]:tw-text-[#3152e8] [&.active]:tw-text-[#3152e8] [&.active]:tw-border-b-[#3152e8] max-[768px]:tw-p-[10px_16px] max-[768px]:tw-text-[14px] settings-tab-item   ${activeTab === "deleted-accounts" ? "active" : ""}`}
                  onClick={() => handleTabChange("deleted-accounts")}
                  disabled={!permissions['settings_account_deletion-view']}
                  title={!permissions['settings_account_deletion-view'] ? "You don't have permission to view deleted accounts" : ""}
                >
                  <Icon
                    icon="fluent:person-delete-24-regular"
                    width="22"
                    height="22"
                    className="tw-me-2"
                  />
                  Deleted Accounts
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="tw-min-h-[400px] tw-overflow-visible settings-content-container">
              {activeTab === "about-organization" && (
                <div className="tw-bg-[white] tw-rounded-[12px] tw-shadow-[0_4px_12px_rgba(0,_0,_0,_0.08)] tw-p-[24px] tw-overflow-visible max-[768px]:tw-p-[16px] settings-card">
                  <AboutOrganization />
                </div>
              )}

              {activeTab === "faq" && (
                <div className="tw-bg-[white] tw-rounded-[12px] tw-shadow-[0_4px_12px_rgba(0,_0,_0,_0.08)] tw-p-[24px] tw-overflow-visible max-[768px]:tw-p-[16px] settings-card">
                  <FAQ />
                </div>
              )}

              {activeTab === "notifications" && (
                <div className="tw-bg-[white] tw-rounded-[12px] tw-shadow-[0_4px_12px_rgba(0,_0,_0,_0.08)] tw-p-[24px] tw-overflow-visible max-[768px]:tw-p-[16px] settings-card">
                  <Notifications showMasterNotificationControl={showMasterNotificationControl} />
                </div>
              )}

              {activeTab === "announcement" && (
                <div className="tw-bg-[white] tw-rounded-[12px] tw-shadow-[0_4px_12px_rgba(0,_0,_0,_0.08)] tw-p-[24px] tw-overflow-visible max-[768px]:tw-p-[16px] settings-card">
                  <Announcement />
                </div>
              )}

              {activeTab === "query" && (
                <div className="tw-bg-[white] tw-rounded-[12px] tw-shadow-[0_4px_12px_rgba(0,_0,_0,_0.08)] tw-p-[24px] tw-overflow-visible max-[768px]:tw-p-[16px] settings-card">
                  <Query />
                </div>
              )}

              {isPaymentEnabled && activeTab === "payment-history" && (
                <div className="tw-bg-[white] tw-rounded-[12px] tw-shadow-[0_4px_12px_rgba(0,_0,_0,_0.08)] tw-p-[24px] tw-overflow-visible max-[768px]:tw-p-[16px] settings-card">
                  <PaymentHistory />
                </div>
              )}

              {activeTab === "deleted-accounts" && permissions['settings_account_deletion-view'] && (
                <div className="tw-bg-[white] tw-rounded-[12px] tw-shadow-[0_4px_12px_rgba(0,_0,_0,_0.08)] tw-p-[24px] tw-overflow-visible max-[768px]:tw-p-[16px] settings-card">
                  <DeletedAccounts />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
